import {
  Box,
  Typography,
  Button,
  AppBar,
  IconButton,
  Tooltip,
} from '@mui/material'
import { Icon } from 'components'
import { theme } from 'config'
import { Step } from 'models'
import React, { useState } from 'react'
import { StepFilesModal } from 'views/editor/stepFilesModal'
import { StepSettingsModal } from 'views/editor/stepSettingsModal'
import { observer } from 'mobx-react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

export interface IEditorTopbarProps {
  showBackButton?: boolean,
  title?:          string,
  actionLabel?:    string,
  actionDisabled?: boolean,
  hideAction?:     boolean,
  showSettings?:   boolean,
  currentStep?:    Step,
  canEdit:         boolean,
  onAction?:       () => void,
  onBack?():       void,
}

export const EditorTopbar = observer((props: IEditorTopbarProps): JSX.Element => {
  const { t } = useTranslation()
  const {
    onAction,
    actionLabel,
    actionDisabled,
    title,
    hideAction,
    showSettings,
    currentStep,
    canEdit,
    onBack,
    showBackButton,
  } = props

  const [action, setAction] = useState<undefined | { modal: 'settings' | 'files' }>(undefined)

  return (
    <>
      <AppBar
        position = 'sticky'
        sx       = {{
          width: `calc(100% - ${theme.drawerWidth})`,
          ml:    `${theme.drawerWidth}`,
        }}
      >
        <Box
          display        = 'flex'
          justifyContent = 'space-between'
          alignItems     = 'center'
        >
          {showBackButton && (
            <MenuTrigger
              display    = 'flex'
              alignItems = 'center'
              gap        = {1}
              p          = {1}
              onClick    = {onBack}
            >
              <Icon iconId='arrow-left' />
              {t('back')}
            </MenuTrigger>
          )}
          <Box>
            <Typography variant='h3'>{title}</Typography>
          </Box>

          <Box gap={1}>
            {currentStep && currentStep !== undefined && showSettings && (
              <>
                <Tooltip title={t('files')} arrow>
                  <IconButton
                    onClick = {() => setAction({ modal: 'files' })}
                    sx      = {{ position: 'relative' }}
                  >
                    <Icon iconId='files' size='size-24' />
                    {currentStep.getDocuments.length > 0 && (
                      <FileCount>{currentStep.getDocuments.length}</FileCount>
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('settings')} arrow>
                  <IconButton
                    onClick={() => setAction({ modal: 'settings' })}
                  >
                    <Icon iconId='settings' size='size-24' />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {!hideAction && (
              <Button
                sx       = {{marginLeft: 2}}
                disabled = {actionDisabled}
                variant  = 'contained'
                onClick  = {onAction}
              >
                {actionLabel}
              </Button>
            )}
          </Box>
        </Box>
      </AppBar>
      {action?.modal === 'settings' && (
        <StepSettingsModal
          isOpen
          currentStep = {currentStep}
          canEdit     = {canEdit}
          onClose     = {() => setAction(undefined)}
        />
      )}
      {action?.modal === 'files' && (
        <StepFilesModal
          isOpen
          targetStep = {currentStep}
          onClose     = {() => setAction(undefined)}
        />
      )}
    </>
  );
});

const FileCount = styled.span`
  position:         absolute;
  top:              0.5rem;
  right:            0.5rem;
  width:            1.4rem;
  height:           1.4rem;
  border-radius:    50%;
  display:          flex;
  justify-content:  center;
  align-items:      center;
  background-color: black;
  color:            white;
  font-weight:      bold;
  font-size:        0.875rem;
`;

const MenuTrigger = styled(Box)`
  cursor: pointer;
`;